'use client'

/**
 * Doorlopende camera-scanner (batch-69 samenstellen; sinds batch-71 gedeeld tussen de
 * portal, de backoffice en de magazijn-tablet). Anders dan de losse /scanner navigeert
 * hij niet na een scan: elke herkende QR gaat naar `onScan` en de camera blijft aan, zodat
 * je met de telefoon in de hand (en handschoenen aan) het hele schap afloopt.
 *
 * - Dezelfde code binnen 2,5 seconde telt één keer: een label blijft even in beeld en mag
 *   niet twee keer afvinken. Een tweede stuk van hetzelfde artikel scan je na die pauze,
 *   of je zet het aantal hoger.
 * - Tijdens het verwerken (`bezig`) wordt niets nieuws doorgegeven.
 * - Camera uit als het scherm weggaat (accu); terug op het scherm start hij weer als hij
 *   aan stond.
 * - Typen blijft altijd mogelijk (label onleesbaar, of een handscanner die als
 *   toetsenbord werkt).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { extractQrToken } from '../../lib/qr-token'
import { useQrCamera, type LaadZxing } from './use-qr-camera'

const ZELFDE_CODE_PAUZE_MS = 2500

export function QrScanner({
  onScan,
  laadZxing,
  bezig = false,
  hint = 'Richt de camera op het label',
  autoStart = true,
}: {
  onScan: (token: string) => void | Promise<void>
  /** `() => import('@zxing/browser')` vanuit de app: de terugval als er geen BarcodeDetector is. */
  laadZxing: LaadZxing
  bezig?: boolean
  hint?: string
  autoStart?: boolean
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const wasAanRef = useRef(false)
  const laatsteRef = useRef<{ token: string; op: number } | null>(null)
  const bezigRef = useRef(bezig)
  const onScanRef = useRef(onScan)
  bezigRef.current = bezig
  onScanRef.current = onScan

  const [typen, setTypen] = useState(false)
  const [code, setCode] = useState('')
  const [codeFout, setCodeFout] = useState<string | null>(null)
  const invoerRef = useRef<HTMLInputElement>(null)

  const aanbieden = useCallback((tekst: string) => {
    const token = extractQrToken(tekst)
    if (!token || bezigRef.current) return
    const nu = Date.now()
    const vorige = laatsteRef.current
    if (vorige && vorige.token === token && nu - vorige.op < ZELFDE_CODE_PAUZE_MS) return
    laatsteRef.current = { token, op: nu }
    void onScanRef.current(token)
  }, [])

  const camera = useQrCamera({ videoRef, onTekst: aanbieden, laadZxing })
  const { stand, setStand, foutTekst, start, stop, isActief, zaklamp, zaklampKan, schakelZaklamp } = camera

  const schakel = useCallback(() => {
    if (isActief() || stand === 'starten') {
      wasAanRef.current = false
      stop()
      setStand('uit')
    } else {
      wasAanRef.current = true
      void start()
    }
  }, [isActief, setStand, stand, start, stop])

  // Starten bij openen (één tik minder op de vloer) en altijd stoppen bij weggaan.
  useEffect(() => {
    if (autoStart) {
      wasAanRef.current = true
      void start()
    }
    return () => stop()
  }, [autoStart, start, stop])

  // Scherm uit of andere app: camera uit (accu, en de camera komt vrij).
  useEffect(() => {
    function zichtbaarheid() {
      if (document.visibilityState === 'hidden') {
        if (isActief()) {
          stop()
          setStand('uit')
        }
      } else if (wasAanRef.current && !isActief()) {
        void start()
      }
    }
    document.addEventListener('visibilitychange', zichtbaarheid)
    return () => document.removeEventListener('visibilitychange', zichtbaarheid)
  }, [isActief, setStand, start, stop])

  function verstuurCode(e: React.FormEvent) {
    e.preventDefault()
    const token = extractQrToken(code)
    if (!token) {
      setCodeFout('Geen geldige code. Typ de code zoals die onder de QR op het label staat.')
      return
    }
    setCodeFout(null)
    setCode('')
    // Typen is een bewuste handeling: geen dubbel-scan-pauze.
    laatsteRef.current = { token, op: Date.now() }
    void onScanRef.current(token)
    invoerRef.current?.focus()
  }

  const cameraAan = stand === 'aan' || stand === 'starten'

  return (
    <div className="flex flex-col gap-2">
      <div className="relative aspect-[4/3] max-h-[42vh] w-full overflow-hidden rounded-xl bg-streep-donker">
        <video
          ref={videoRef}
          className={`absolute inset-0 h-full w-full object-cover ${stand === 'aan' ? '' : 'invisible'}`}
          playsInline
          muted
          autoPlay
          aria-label="Camerabeeld voor het scannen"
        />
        {stand === 'aan' && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <div className="h-[55%] w-[55%] max-w-[240px] rounded-2xl border-4 border-streep-groen/90" />
            <p className="absolute bottom-2 left-0 right-0 px-3 text-center text-sm font-medium text-white drop-shadow">
              {bezig ? 'Bezig met verwerken...' : hint}
            </p>
          </div>
        )}
        {stand === 'starten' && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-white/80">Camera starten...</div>
        )}
        {stand === 'uit' && (
          <button
            type="button"
            onClick={schakel}
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white active:bg-white/10"
          >
            <svg className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h3l2-3h8l2 3h3v13H3V7z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            <span className="text-base font-semibold">Tik om te scannen</span>
          </button>
        )}
        {stand === 'geweigerd' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center text-white">
            <p className="text-base font-semibold">Geen toegang tot de camera</p>
            <p className="text-sm text-white/80">
              Geef STREEP toegang tot de camera in de instellingen van je telefoon, of typ de code van het label.
            </p>
          </div>
        )}
        {stand === 'fout' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center text-white">
            <p className="text-base font-semibold">Camera niet beschikbaar</p>
            <p className="text-sm text-white/80">{foutTekst}</p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={schakel}
          className="min-h-[48px] flex-1 rounded-xl border border-streep-border bg-streep-surface px-4 text-base font-semibold text-streep-donker active:bg-streep-surface-alt"
        >
          {cameraAan ? 'Camera uit' : 'Camera aan'}
        </button>
        {zaklampKan && stand === 'aan' && (
          <button
            type="button"
            onClick={() => void schakelZaklamp()}
            aria-pressed={zaklamp}
            className={`min-h-[48px] rounded-xl px-4 text-base font-semibold ${
              zaklamp ? 'bg-streep-groen text-streep-donker' : 'border border-streep-border bg-streep-surface text-streep-donker'
            }`}
          >
            Lamp
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setTypen((t) => !t)
            window.setTimeout(() => invoerRef.current?.focus(), 50)
          }}
          aria-expanded={typen}
          className="min-h-[48px] flex-1 rounded-xl border border-streep-border bg-streep-surface px-4 text-base font-semibold text-streep-donker active:bg-streep-surface-alt"
        >
          Code typen
        </button>
      </div>

      {typen && (
        <form onSubmit={verstuurCode} className="flex flex-col gap-1">
          <label htmlFor="qr-scanner-code" className="text-sm font-medium text-streep-donker">
            Code van het label
          </label>
          <div className="flex gap-2">
            <input
              id="qr-scanner-code"
              ref={invoerRef}
              value={code}
              onChange={(e) => {
                setCode(e.target.value)
                if (codeFout) setCodeFout(null)
              }}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              enterKeyHint="go"
              aria-invalid={codeFout ? true : undefined}
              className="min-h-[48px] w-full flex-1 rounded-xl border border-streep-border bg-streep-surface px-3 text-base text-streep-donker focus:border-streep-groen focus:outline-none focus:ring-1 focus:ring-streep-groen"
              placeholder="Typ of scan de code"
            />
            <button
              type="submit"
              disabled={bezig || !code.trim()}
              className="min-h-[48px] rounded-xl bg-streep-groen px-4 text-base font-semibold text-streep-donker disabled:opacity-50"
            >
              Verwerk
            </button>
          </div>
          {codeFout && (
            <p role="alert" className="text-sm text-red-700">
              {codeFout}
            </p>
          )}
        </form>
      )}
    </div>
  )
}
